import { toast } from "@assetwatch/ui";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { workClient } from "../clients/workClient";
import { useWorkScope } from "../components/WorkScopeProvider";

type ProcedureLifecycleInput = {
  procedureId: string;
  /** The Procedure's Title, used only in the confirmation toast. */
  name: string;
};

async function readErrorMessage(response: Response, fallback: string) {
  const body = (await response.json().catch(() => null)) as {
    error?: string;
  } | null;
  return body?.error ?? fallback;
}

export function useDeleteProcedureMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ procedureId }: ProcedureLifecycleInput) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].procedures[":procedureId"].$delete({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          procedureId,
        },
      });
      if (!response.ok)
        throw new Error(
          await readErrorMessage(
            response,
            "Unable to delete Procedure. Try again.",
          ),
        );
      return response.json();
    },
    onSuccess: async (_result, { procedureId, name }) => {
      toast.add(`Procedure "${name}" deleted.`, { icon: true });
      // The deleted Procedure is excluded from the ordinary detail read, so it
      // is dropped from the cache instead of refetched into a 404.
      queryClient.removeQueries({
        queryKey: [
          "procedure",
          selectedCustomer?.id,
          selectedFacility?.id,
          procedureId,
          "active",
        ],
      });
      await queryClient.invalidateQueries({ queryKey: ["procedures"] });
    },
    onError: () =>
      toast.error("Unable to delete Procedure. Try again.", { icon: true }),
  });
}

export function useRestoreProcedureMutation() {
  const { selectedCustomer, selectedFacility } = useWorkScope();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ procedureId }: ProcedureLifecycleInput) => {
      if (!selectedCustomer || !selectedFacility)
        throw new Error("A customer and facility must be selected.");
      const response = await workClient.v1.customers[":customerId"].facilities[
        ":facilityId"
      ].procedures[":procedureId"].restore.$post({
        param: {
          customerId: selectedCustomer.id,
          facilityId: selectedFacility.id,
          procedureId,
        },
      });
      if (!response.ok)
        throw new Error(
          await readErrorMessage(
            response,
            "Unable to restore Procedure. Try again.",
          ),
        );
      return response.json();
    },
    onSuccess: async (_result, { procedureId, name }) => {
      toast.add(`Procedure "${name}" restored.`, { icon: true });
      // The panel still observes the deleted-view detail until the list
      // refresh moves the selection, and that read now 404s, so only the
      // active-view detail is marked stale.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["procedures"] }),
        queryClient.invalidateQueries({
          queryKey: [
            "procedure",
            selectedCustomer?.id,
            selectedFacility?.id,
            procedureId,
            "active",
          ],
          exact: true,
        }),
      ]);
    },
    onError: () =>
      toast.error("Unable to restore Procedure. Try again.", { icon: true }),
  });
}
